import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { MailCheck } from 'lucide-react';
import { useT, type MessageKey } from '../../i18n';
import {
  INVITATION_NO_API,
  INVITATION_UNREACHABLE,
  acceptInvitation,
  startInvitation,
  type InvitationContext,
} from '../../lib/api/memberInvitationAccept';
import { Padel24Logo } from '../ui';
import { useRetryWait } from '../ui/useRetryWait';
import { formatOtpCodeInput } from './otpCode';

// ─────────────────────────────────────────────────────────────────────
// THE LANDING FOR A CLUB'S E-MAIL INVITATION (#1069).
//
// A club invites a player by e-mail (#1061). The mail links to
// `/?invitation=<token>`, and this is where that link lands:
//
//   1. OPEN   `start` — the API mails a code to the invited address and says
//             which club and which player the invitation is for;
//   2. CODE   the person types the code they received;
//   3. ACCEPT `accept` — the API links them to the player (and, if the club
//             said so, makes them a member) and sets the member session.
//
// The link proved DELIVERY; the code proves the MAILBOX. Neither alone attaches
// anybody to anything (`memberInvitationAccept.ts`). This is where the player's
// consent of #197 Q4 is given — and nothing before step 3 changes a roster.
//
// ── OPENING IS DONE ONCE ──────────────────────────────────────────
//
// `start` MAILS A CODE. React runs a mount effect twice in development, and a
// remount would run it again, so the call is guarded by a ref: one open, one
// mail. A second code is only ever sent because somebody asked for it.
//
// ── A RIGHT CODE CAN STILL BE REFUSED, AND THEN IT SAYS SO ────────
//
// `accept` answers `verified: true` when the code was right but the invitation
// cannot be used — the player was claimed by somebody else, this account
// already has a player, another club rostered them meanwhile. The address IS
// proven and stays proven, so the screen says «your address is confirmed, but»
// rather than «wrong code», which would send the person back to a code that was
// never the problem.
//
// ── THE SESSION IS RESTORED BY THE GATE, NOT INSTALLED HERE ──────
//
// `accept` sets the `spm_` cookie. `onAccepted` lets the gate ask `/member/me`
// — the same path a reload takes — rather than this screen building a session
// out of what it was told. One place owns what «signed in» means.
// ─────────────────────────────────────────────────────────────────────

/** Mirrors `OTP_LIFETIME_MS`. For the sentence, not for any decision. */
const CODE_LIFETIME_MINUTES = 10;

export interface MemberInvitationScreenProps {
  /** The token from the link. Read once by the gate; never shown. */
  token: string;
  /** The invitation was accepted and the member cookie is set. */
  onAccepted: () => void | Promise<void>;
  /** The person left — declined for now, or the invitation was unusable. */
  onDismiss: () => void;
}

type Step =
  | { name: 'opening' }
  | { name: 'closed'; message: MessageKey }
  | { name: 'code'; error: MessageKey | null; resent: boolean }
  | { name: 'sending' }
  | { name: 'accepting' }
  | { name: 'refused'; reason: MessageKey };

/** A refusal of `start` (or of a failed resend) as a sentence. Codes are never shown. */
export function startRefusalKey(code: string): MessageKey {
  switch (code) {
    case 'invitation_not_valid':
      return 'memberInvitation.notValid';
    case 'identity_ambiguous':
      return 'memberInvitation.identityAmbiguous';
    case 'mail_failed':
      return 'memberInvitation.mailFailed';
    case 'not_configured':
    case INVITATION_NO_API:
      return 'memberInvitation.unavailable';
    case INVITATION_UNREACHABLE:
      return 'memberInvitation.network';
    default:
      return 'memberInvitation.unknown';
  }
}

/**
 * A refusal of `accept` whose CODE WAS RIGHT (`verified: true`): the address is
 * proven, the invitation is what cannot be used.
 */
export function verifiedRefusalKey(code: string): MessageKey {
  switch (code) {
    case 'player_already_claimed':
      return 'memberInvitation.refused.playerClaimed';
    case 'account_already_linked':
      return 'memberInvitation.refused.accountLinked';
    case 'player_on_another_roster':
      return 'memberInvitation.refused.otherRoster';
    default:
      return 'memberInvitation.refused.notValid';
  }
}

export function MemberInvitationScreen({
  token,
  onAccepted,
  onDismiss,
}: MemberInvitationScreenProps) {
  const t = useT();
  const errorId = useId();
  const [step, setStep] = useState<Step>({ name: 'opening' });
  const [context, setContext] = useState<InvitationContext | null>(null);
  const [code, setCode] = useState('');
  const wait = useRetryWait<'send' | 'accept'>();
  const opened = useRef(false);

  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    void startInvitation(token).then((outcome) => {
      if (outcome.code === null && outcome.context) {
        setContext(outcome.context);
        setStep({ name: 'code', error: null, resent: false });
        return;
      }
      if (outcome.code === 'too_many_attempts') {
        // Nothing to show yet — no context to name — so the wait is the
        // message, and the person can try the link again once it has passed.
        wait.start('send', outcome.retryAfterSeconds ?? 0);
        setStep({ name: 'closed', message: 'memberInvitation.unknown' });
        return;
      }
      setStep({ name: 'closed', message: startRefusalKey(outcome.code ?? '') });
    });
    // `wait` is stable for the life of the component; the open runs once by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const busy = step.name === 'opening' || step.name === 'sending' || step.name === 'accepting';

  const resend = async () => {
    if (busy || wait.waiting('send')) return;
    wait.release('send');
    setStep({ name: 'sending' });
    const outcome = await startInvitation(token);
    if (outcome.code === null) {
      setCode('');
      setStep({ name: 'code', error: null, resent: true });
      return;
    }
    if (outcome.code === 'too_many_attempts') wait.start('send', outcome.retryAfterSeconds ?? 0);
    setStep({
      name: 'code',
      error: outcome.code === 'too_many_attempts' ? null : startRefusalKey(outcome.code),
      resent: false,
    });
  };

  const accept = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || wait.waiting('accept')) return;
    wait.release('accept');
    setStep({ name: 'accepting' });
    const outcome = await acceptInvitation(token, code.trim());
    if (outcome.code === null) {
      await onAccepted();
      return;
    }
    if (outcome.verified) {
      setStep({ name: 'refused', reason: verifiedRefusalKey(outcome.code) });
      return;
    }
    if (outcome.code === 'too_many_attempts') wait.start('accept', outcome.retryAfterSeconds ?? 0);
    const error: MessageKey | null =
      outcome.code === 'too_many_attempts'
        ? null
        : outcome.code === 'not_valid' || outcome.code === 'invitation_not_valid'
          ? 'memberInvitation.wrongCode'
          : outcome.code === 'identity_ambiguous'
            ? 'memberInvitation.identityAmbiguous'
            : outcome.code === INVITATION_UNREACHABLE
              ? 'memberInvitation.network'
              : outcome.code === INVITATION_NO_API || outcome.code === 'not_configured'
                ? 'memberInvitation.unavailable'
                : 'memberInvitation.unknown';
    setStep({ name: 'code', error, resent: false });
  };

  const stepError = step.name === 'code' && step.error ? t(step.error) : null;
  const error = stepError ?? wait.notice;
  const onCodeStep = step.name === 'code' || step.name === 'sending' || step.name === 'accepting';

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: '#6b21a8' }}
    >
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <Padel24Logo size="xl" variant="full" tone="light" />
        </div>

        <div className="card p-6 space-y-3">
          <div className="flex items-center gap-2">
            <MailCheck className="w-5 h-5 text-ink-3" aria-hidden="true" />
            <h1 className="font-display text-lg font-semibold">
              {step.name === 'refused'
                ? t('memberInvitation.refused.title')
                : context
                  ? t('memberInvitation.title', { club: context.clubName })
                  : t('memberInvitation.titleGeneric')}
            </h1>
          </div>

          {step.name === 'opening' && (
            <p className="text-sm text-ink-3" role="status">
              {t('memberInvitation.opening')}
            </p>
          )}

          {step.name === 'closed' && (
            <>
              <p role="alert" className="text-sm text-danger">
                {wait.notice ?? t(step.message)}
              </p>
              <button type="button" className="btn-primary w-full" onClick={onDismiss}>
                {t('memberInvitation.toSignIn')}
              </button>
            </>
          )}

          {step.name === 'refused' && (
            <>
              <p role="alert" className="text-sm">
                {t(step.reason)}
              </p>
              <button type="button" className="btn-primary w-full" onClick={onDismiss}>
                {t('memberInvitation.toSignIn')}
              </button>
            </>
          )}

          {onCodeStep && context && (
            <>
              <p className="text-xs text-ink-3">
                {t(
                  context.grantsMembership
                    ? 'memberInvitation.leadMember'
                    : 'memberInvitation.leadLink',
                  {
                    club: context.clubName,
                    player: context.playerName,
                    minutes: CODE_LIFETIME_MINUTES,
                  },
                )}
              </p>
              <form onSubmit={accept} className="space-y-3">
                <label className="block">
                  <span className="block text-xs font-mono uppercase tracking-wider text-ink-3 mb-1.5">
                    {t('memberInvitation.codeLabel')}
                  </span>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => {
                      setCode(formatOtpCodeInput(e.target.value));
                      wait.release('accept');
                    }}
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                    className="input w-full text-center tracking-[0.3em] text-lg"
                    disabled={busy}
                  />
                </label>
                {error && (
                  <p id={errorId} role="alert" className="text-xs text-danger">
                    {error}
                  </p>
                )}
                {step.name === 'code' && step.resent && !error && (
                  <p className="text-xs text-ink-3">{t('memberInvitation.resent')}</p>
                )}
                <button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={busy || code.trim() === '' || wait.waiting('accept')}
                >
                  {step.name === 'accepting'
                    ? t('memberInvitation.accepting')
                    : t('memberInvitation.accept')}
                </button>
                <button
                  type="button"
                  className="btn-ghost w-full text-xs"
                  onClick={() => void resend()}
                  disabled={busy || wait.waiting('send')}
                >
                  {t('memberInvitation.resend')}
                </button>
                <button
                  type="button"
                  className="btn-ghost w-full text-xs"
                  onClick={onDismiss}
                  disabled={busy}
                >
                  {t('memberInvitation.notNow')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
